import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { OrgLevelProductsAPI } from "@ai360/core";

import { messages as adminMessages } from "~/admin/setup/i18n-messages";
import { preventBubbleUp, toCamelCase } from "~/admin/utils";
import { DataTable, DialogBox, DialogBoxFooterType, Loader, LoaderTypes } from "~/core";
import ApiResultsWrapper from "~/core/api/components/api-results-wrapper";
import { ISearchData, ISortOptions } from "~/core/components/tables/interfaces";
import { setBreadcrumbs } from "~/sliding-panel/data/actions";
import { useAppDispatch } from "~/store/hooks";

import { OrgLevelProductsAddEditPanel } from "./add-edit/add-edit-panel";
import { model, service } from "./data";
import { messages } from "./i18n-messages";

import "./org-level-products.css";

type IOrgLevelProductGridRow = OrgLevelProductsAPI.IOrgLevelProductGridRow;

type OrgLevelProductsFilters = {
    productNames?: string;
    orgLevelNames?: string;
    modifiedDate?: string;
};

type OrgLevelProductGridRow = IOrgLevelProductGridRow & {
    canDelete: boolean;
    productNames: string;
    orgLevelNames: string;
};

interface IOrgLevelProductsProps {
    intl: intlShape;
}

const OrgLevelProducts_ = ({ intl: { formatMessage } }: IOrgLevelProductsProps): ReactElement => {
    const dispatch = useAppDispatch();
    const [filter, setFilter] = useState<OrgLevelProductsFilters>({});
    const [showAddEditPanel, setShowAddEditPanel] = useState(false);
    const [autoSearchData, setAutoSearchData] = useState<ISearchData>();
    const [selectedRow, setSelectedRow] = useState<IOrgLevelProductGridRow | null>(null);
    const [sortOptions, setSortOptions] = useState<ISortOptions>(null);
    const [itemsToDelete, setItemsToDelete] = useState<IOrgLevelProductGridRow[] | null>(null);

    const { data = { model: [] as IOrgLevelProductGridRow[] }, isFetching: isGetLoading } =
        OrgLevelProductsAPI.useGetOrgLevelProductsQuery({
            CompanyGuid: null,
        });

    const [deleteOrgLevelProduct, { isLoading: isDeleteLoading }] =
        OrgLevelProductsAPI.useDeleteOrgLevelProductMutation();

    useEffect(() => {
        dispatch(setBreadcrumbs());
    }, [showAddEditPanel]);

    const products = useMemo(() => {
        return new Set(data.model.map((record) => record.products.map((p) => p.name)).flat());
    }, [data]);
    const orgLevels = useMemo(() => {
        return new Set(data.model.map((record) => record.orgLevels.map((l) => l.name)).flat());
    }, [data]);

    const gridData = useMemo(() => {
        let filteredData = data.model.map(
            (record) =>
                ({
                    ...record,
                    products: [...record.products].sort((a, b) => a.name.localeCompare(b.name)),
                    orgLevels: [...record.orgLevels].sort((a, b) => a.name.localeCompare(b.name)),
                    productNames: record.products
                        .map((p) => p.name)
                        .sort()
                        .join(", "),
                    orgLevelNames: record.orgLevels
                        .map((l) => l.name)
                        .sort()
                        .join(", "),
                    modifiedDate: record.modifiedDate || record.createdDate,
                    canDelete: true,
                } as OrgLevelProductGridRow)
        );
        if (filter.productNames) {
            filteredData = filteredData.filter((record) =>
                record.products.some((p) => p.name.includes(filter.productNames))
            );
        }
        if (filter.orgLevelNames) {
            filteredData = filteredData.filter((record) =>
                record.orgLevels.some((l) => l.name.includes(filter.orgLevelNames))
            );
        }
        if (filter.modifiedDate) {
            filteredData = filteredData.filter((record) =>
                record.modifiedDate.includes(filter.modifiedDate)
            );
        }

        const sortAttribute = toCamelCase(
            sortOptions?.sortOptions?.FieldName || model.PROPS_PRODUCT_NAMES
        );
        const sortAsc = (sortOptions?.sortOptions?.Sort?.Direction || "ASC") === "ASC";
        filteredData = filteredData.sort((a, b) => {
            const aAttr =
                sortAttribute === model.PROPS_MODIFIED_DATE
                    ? new Date(a.modifiedDate)
                    : a[sortAttribute];
            const bAttr =
                sortAttribute === model.PROPS_MODIFIED_DATE
                    ? new Date(b.modifiedDate)
                    : b[sortAttribute];
            if (aAttr < bAttr) {
                return sortAsc ? -1 : 1;
            }
            if (aAttr > bAttr) {
                return sortAsc ? 1 : -1;
            }
            return 0;
        });

        return filteredData;
    }, [data, filter, sortOptions]);

    const getAutoSearchList = (attr: string, value: string) => {
        const attrName = toCamelCase(attr);
        const autoSearchData: ISearchData = {
            searchName: attrName,
            response: [],
        };
        if (attrName === model.PROPS_PRODUCT_NAMES) {
            autoSearchData.response = Array.from(products)
                .filter((product) => product.toLowerCase().includes(value.toLowerCase()))
                .map((product) => ({ value: product }));
        } else if (attrName === model.PROPS_ORG_LEVEL_NAMES) {
            autoSearchData.response = Array.from(orgLevels)
                .filter((level) => level.toLowerCase().includes(value.toLowerCase()))
                .map((level) => ({ value: level }));
        } else if (attrName === model.PROPS_MODIFIED_DATE) {
            autoSearchData.response = Array.from(
                new Set(gridData.map((record) => record.modifiedDate))
            )
                .filter((date) => date.toLowerCase().includes(value.toLowerCase()))
                .map((date) => ({ value: date }));
        }
        setAutoSearchData(autoSearchData);
        return autoSearchData;
    };

    const onFilterChange = (attr: string, value: string, a: OrgLevelProductsFilters) => {
        const attrName = toCamelCase(attr);
        const filterQuery = { ...filter, [attrName]: value ? value : undefined };
        setFilter(filterQuery);
    };

    const onDelete = () => {
        if (itemsToDelete === null) {
            return;
        }
        const deletePromises = itemsToDelete.map((item) =>
            deleteOrgLevelProduct({ productOrgLevelGuid: item.productOrgLevelGuid })
                .unwrap()
                .catch(() => {
                    // Handled by ApiResultsWrapper
                })
        );
        Promise.all(deletePromises).then(() => {
            setItemsToDelete(null);
        });
    };

    const onDeleteAction = (deleteData) => {
        if (deleteData.selectedItems?.length === 0) return;
        const items = Array.isArray(deleteData.selectedItems)
            ? deleteData.selectedItems
            : [deleteData.selectedItems];
        setItemsToDelete(items as IOrgLevelProductGridRow[]);
    };

    const onShowAddEditPanel = (
        _,
        action: "ADD" | "EDIT",
        editData: { productOrgLevelGuid: string }
    ) => {
        let record: IOrgLevelProductGridRow | null = null;
        if (action === "EDIT") {
            record = data.model.find(
                (record) => record.productOrgLevelGuid === editData.productOrgLevelGuid
            );
        }
        setSelectedRow(record);
        setShowAddEditPanel(true);
    };

    const isLoading = isGetLoading || isDeleteLoading;

    return (
        <ApiResultsWrapper clearErrorOnUnmount={false}>
            <div className="content-table-container org-level-products-table">
                {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
                <DataTable
                    autoSearchList={autoSearchData}
                    isEditable
                    isCheckbox={false}
                    service={service}
                    messages={adminMessages}
                    getAutoSearchList={getAutoSearchList}
                    onFilterChange={onFilterChange}
                    setLastEditedRow={() => {
                        /* purposely left blank */
                    }}
                    deleteSelected={onDeleteAction}
                    records={gridData}
                    openAddEditPanel={onShowAddEditPanel}
                    totalCount={gridData.length}
                    onSortOption={setSortOptions}
                />
                {!showAddEditPanel ? null : (
                    <form onSubmit={(event) => preventBubbleUp(event)}>
                        <OrgLevelProductsAddEditPanel
                            closePanel={() => {
                                setSelectedRow(null);
                                setShowAddEditPanel(false);
                            }}
                            selectedRow={selectedRow}
                        />
                    </form>
                )}
            </div>
            <DialogBox
                action={"Delete"}
                draggable={true}
                className="delete-confirmation-modal"
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                isOpen={itemsToDelete !== null}
                onAction={onDelete}
                onClose={() => {
                    console.log("onClose", itemsToDelete);
                    setItemsToDelete(null);
                }}
                title={formatMessage(messages.deleteConfirmationTitle)}
            >
                <p>{formatMessage(messages.deleteConfirmationMessage)}</p>
            </DialogBox>
        </ApiResultsWrapper>
    );
};

export const OrgLevelProducts = injectIntl(OrgLevelProducts_);
